import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { repriceGeneratedRun } from "../../../../pages/master/billing/customerBillingCalc";
import { toast } from "react-toastify";

import { startLoader, stopLoader } from "../../../../utils/commonFunctions";
import { BillingActionTypes, BillingActions } from "../../../actionTypes/master/billingActionTypes";
import {
  deleteBillingApi,
  editBillingDataByIdApi,
  generateBillingApi,
  getBillingBulkRunByIdApi,
  getBillingBulkRunsApi,
  getBillingDataByIdApi,
  getBillingListApi,
  updateBillingQcApi,
} from "../../requests/billing/billingRequests";
import {
  setBillingDataById,
  setBillingHistory,
  setBillingHistoryById,
  setBillingHistoryPageCount,
  setBillingList,
  setBillingListPageCount,
  setGeneratedBilling,
} from "../../../actions/master/billingActions";
import { setPostApiStatus, setPutApiStatus } from "../../../actions/apiStatusActions";
import { AppState } from "../../../types";
import { IBillingListSearch, IGenerateBilling } from "../../../../types/master/billingInterface";


function* getBillingListHandler(action: BillingActionTypes) {
  yield delay(500);
  try {
    yield startLoader("transparent");

    const { data } = yield call(getBillingListApi, action.payload);

    if (data.status) {
      yield put(setBillingList(data?.data?.rows ?? []));
      yield put(setBillingListPageCount(data?.data?.count ?? 0));
    } else {
      yield put(setBillingList([]));
      yield put(setBillingListPageCount(0));
      toast.error(data?.message, {
        position: "top-right",
      });
    }
  } catch (error: any) {
    yield put(setBillingList([]));
    yield put(setBillingListPageCount(0));
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* generateBillingHandler(action: BillingActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(generateBillingApi, action.payload);

    if (data.status) {
      toast.success(data?.message, {
        position: "top-right",
      });
      // runs in the background: once the run finishes, the bills it created are priced from customer rates
      if (data?.data?.id) repriceGeneratedRun(data.data, action.payload as IGenerateBilling);
      yield put(setGeneratedBilling(null));
      yield put(setPostApiStatus(true));
    } else {
      yield put(setGeneratedBilling(null));
      toast.error(data?.message, {
        position: "top-right",
      });
      yield put(setPostApiStatus(false));
    }
  } catch (error: any) {
    yield put(setGeneratedBilling(null));
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
    yield put(setPostApiStatus(false));
  } finally {
    yield stopLoader("transparent");
  }
}

function* getBillingDataByIdHandler(action: BillingActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(getBillingDataByIdApi, action.payload);
    if (data.status) {
      yield put(setBillingDataById(data?.data));
    } else {
      toast.error(data?.message, {
        position: "top-right",
      });
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* editBillingDataByIdHandler(action: BillingActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(editBillingDataByIdApi, action.payload);
    if (!data.status) {
      toast.error(data?.message, {
        position: "top-right",
      });
      yield put(setPutApiStatus(false));
      return;
    }

    toast.success(data.message, {
      position: "top-right",
    });

    if (data?.data) {
      yield put(setBillingDataById(data.data));
    }

    const { data: qcData } = yield call(updateBillingQcApi, {
      id: action.payload.id,
      data: action.payload.qc,
    });

    if (qcData.status) {
      if (data?.data && qcData?.data) {
        yield put(
          setBillingDataById({
            ...data.data,
            billing_qc_status: qcData.data.billing_qc_status,
            billing_qc_remarks: qcData.data.billing_qc_remarks,
            billing_qc_by: qcData.data.billing_qc_by,
          })
        );
      }
      yield put(setPutApiStatus(true));
    } else {
      toast.error(qcData?.message, {
        position: "top-right",
      });
      yield put(setPutApiStatus(false));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
    yield put(setPutApiStatus(false));
  } finally {
    yield stopLoader("transparent");
  }
}

function* refreshBillingList() {
  const lastListQuery: IBillingListSearch | null = yield select(
    (state: AppState) => state.master.billing?.lastListQuery ?? null
  );

  if (!lastListQuery) return;

  try {
    const { data } = yield call(getBillingListApi, lastListQuery);

    if (data.status) {
      yield put(setBillingList(data?.data?.rows ?? []));
      yield put(setBillingListPageCount(data?.data?.count ?? 0));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  }
}

function* deleteBillingHandler(action: BillingActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(deleteBillingApi, action.payload);

    if (data.status) {
      toast.success(data?.message || "Billing deleted successfully.", {
        position: "top-right",
      });
      yield call(refreshBillingList);
    } else {
      toast.error(data?.message, {
        position: "top-right",
      });
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Failed to delete billing.", {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* getBillingHistoryHandler(action: BillingActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(getBillingBulkRunsApi, action.payload);

    if (data.status) {
      yield put(setBillingHistory(data?.data?.rows ?? []));
      yield put(setBillingHistoryPageCount(data?.data?.count ?? 0));
    } else {
      yield put(setBillingHistory([]));
      yield put(setBillingHistoryPageCount(0));
      toast.error(data?.message, {
        position: "top-right",
      });
    }
  } catch (error: any) {
    yield put(setBillingHistory([]));
    yield put(setBillingHistoryPageCount(0));
    toast.error(error?.response?.data?.message || "Failed to fetch billing history.", {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* getBillingHistoryByIdHandler(action: BillingActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(getBillingBulkRunByIdApi, action.payload);

    if (data.status) {
      yield put(setBillingHistoryById(data?.data ?? null));
    } else {
      yield put(setBillingHistoryById(null));
      toast.error(data?.message, {
        position: "top-right",
      });
    }
  } catch (error: any) {
    yield put(setBillingHistoryById(null));
    toast.error(error?.response?.data?.message || "Failed to fetch billing history details.", {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

export default function* billingSaga() {
  yield takeLatest(BillingActions.GET_BILLING_LIST, getBillingListHandler);
  yield takeLatest(BillingActions.GENERATE_BILLING, generateBillingHandler);
  yield takeLatest(BillingActions.GET_BILLING_DATA_BY_ID, getBillingDataByIdHandler);
  yield takeLatest(BillingActions.EDIT_BILLING_DATA_BY_ID, editBillingDataByIdHandler);
  yield takeLatest(BillingActions.DELETE_BILLING, deleteBillingHandler);
  yield takeLatest(BillingActions.GET_BILLING_HISTORY, getBillingHistoryHandler);
  yield takeLatest(BillingActions.GET_BILLING_HISTORY_BY_ID, getBillingHistoryByIdHandler);
}
